import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Eye, Edit, Copy, Trash2, FileDown, Search, FileText, LayoutGrid, List, Check, X, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const badgeVariants = {
  Borrador: 'secondary',
  Enviada: 'default',
  Adjudicada: 'outline',
};

const Publicaciones = () => {
  const [vehiculos, setVehiculos] = useState([]);
  const [filteredVehiculos, setFilteredVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'cards' o 'list' - default: list
  const [selectedVehiculoOfertas, setSelectedVehiculoOfertas] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [revendedores, setRevendedores] = useState({});
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    loadVehiculos();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [vehiculos, searchTerm, estadoFilter, sortField, sortDirection]);

  const loadVehiculos = async () => {
    try {
      const [vehiculosRes, ofertasRes, revendedoresRes] = await Promise.all([
        axios.get(`${API}/vehiculos`),
        axios.get(`${API}/ofertas`),
        axios.get(`${API}/revendedores`),
      ]);
      setVehiculos(vehiculosRes.data);
      setOfertas(ofertasRes.data);
      
      const revMap = {};
      revendedoresRes.data.forEach(r => { revMap[r.id] = r; });
      setRevendedores(revMap);
    } catch (error) {
      console.error('Error loading vehiculos:', error);
      toast.error('Error al cargar publicaciones');
    } finally {
      setLoading(false);
    }
  };

  const getOfertasCount = (vehiculoId) => {
    return ofertas.filter(o => o.vehiculoId === vehiculoId).length;
  };

  const handleViewOfertas = (vehiculo) => {
    setSelectedVehiculoOfertas(vehiculo);
  };

  const handleAcceptOffer = async (ofertaId) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Aceptada' });
      toast.success('Oferta aceptada');
      await loadVehiculos();
      // Actualizar el vehículo seleccionado
      const updatedVehiculo = await axios.get(`${API}/vehiculos/${selectedVehiculoOfertas.id}`);
      setSelectedVehiculoOfertas(updatedVehiculo.data);
    } catch (error) {
      console.error('Error accepting offer:', error);
      toast.error('Error al aceptar oferta');
    }
  };

  const handleRejectOffer = async (ofertaId) => {
    try {
      await axios.put(`${API}/ofertas/${ofertaId}`, { estado: 'Rechazada' });
      toast.success('Oferta rechazada');
      await loadVehiculos();
    } catch (error) {
      console.error('Error rejecting offer:', error);
      toast.error('Error al rechazar oferta');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const applyFilters = () => {
    let filtered = [...vehiculos];

    // Filtro de búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.marca.toLowerCase().includes(term) ||
          v.modelo.toLowerCase().includes(term) ||
          v.autor.toLowerCase().includes(term)
      );
    }

    // Filtro de estado
    if (estadoFilter !== 'all') {
      filtered = filtered.filter((v) => v.estadoPublicacion === estadoFilter);
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      let aVal, bVal;

      if (sortField === 'vehiculo') {
        aVal = `${a.marca} ${a.modelo}`.toLowerCase();
        bVal = `${b.marca} ${b.modelo}`.toLowerCase();
      } else if (sortField === 'anio') {
        aVal = a.anio;
        bVal = b.anio;
      } else if (sortField === 'km') {
        aVal = a.km;
        bVal = b.km;
      } else if (sortField === 'precioBase') {
        aVal = a.precioBase;
        bVal = b.precioBase;
      } else if (sortField === 'createdAt') {
        aVal = new Date(a.createdAt);
        bVal = new Date(b.createdAt);
      } else if (sortField === 'autor') {
        aVal = a.autor.toLowerCase();
        bVal = b.autor.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredVehiculos(filtered);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/vehiculos/${id}`);
      toast.success('Publicación eliminada');
      loadVehiculos();
    } catch (error) {
      console.error('Error deleting vehiculo:', error);
      toast.error('Error al eliminar publicación');
    }
    setDeleteId(null);
  };

  const handleExportPDF = () => {
    toast.info('Exportación PDF iniciada (demo)');
  };

  const handleExportExcel = () => {
    toast.info('Exportación Excel iniciada (demo)');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-background min-h-screen">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-semibold tracking-tight">Publicaciones</h1>
              <p className="text-muted-foreground mt-1">
                {filteredVehiculos.length} publicaciones
              </p>
            </div>
            <div className="flex gap-2">
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-r-none"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button asChild data-testid="new-listing-button">
                <Link to="/admin/publicaciones/nueva">
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Publicación
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Filtros */}
        <Card className="p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por marca, modelo o autor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={estadoFilter} onValueChange={setEstadoFilter}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="estado-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Borrador">Borrador</SelectItem>
                <SelectItem value="Enviada">Enviada</SelectItem>
                <SelectItem value="Adjudicada">Adjudicada</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                data-testid="export-pdf-button"
              >
                <FileDown className="h-4 w-4 mr-2" />
                PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                data-testid="export-excel-button"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </Card>

        {/* Lista de publicaciones */}
        {filteredVehiculos.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <FileText className="h-12 w-12 text-muted-foreground" />
              <div>
                <h3 className="text-lg font-semibold mb-1">
                  {vehiculos.length === 0 ? 'No hay publicaciones' : 'No se encontraron resultados'}
                </h3>
                <p className="text-muted-foreground">
                  {vehiculos.length === 0
                    ? 'Comienza creando tu primera publicación'
                    : 'Intenta con otros filtros de búsqueda'}
                </p>
              </div>
              {vehiculos.length === 0 && (
                <Button asChild>
                  <Link to="/admin/publicaciones/nueva">
                    <Plus className="h-4 w-4 mr-2" />
                    Crear Primera Publicación
                  </Link>
                </Button>
              )}
            </div>
          </Card>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-4">
            {filteredVehiculos.map((vehiculo) => (
              <Card key={vehiculo.id} className="p-4 hover:shadow-md transition-shadow" data-testid="vehiculo-card">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Imagen */}
                  <div className="w-full sm:w-32 h-32 rounded-md overflow-hidden bg-muted flex-shrink-0">
                    {vehiculo.fotos && vehiculo.fotos[0] ? (
                      <img
                        src={vehiculo.fotos[0]}
                        alt={`${vehiculo.marca} ${vehiculo.modelo}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Información */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold">
                          {vehiculo.marca} {vehiculo.modelo}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {vehiculo.version} • {vehiculo.anio} • {vehiculo.km.toLocaleString()} km
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Autor: {vehiculo.autor}
                        </p>
                      </div>
                      <Badge variant={badgeVariants[vehiculo.estadoPublicacion]} data-testid="listing-status-badge">
                        {vehiculo.estadoPublicacion}
                      </Badge>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-lg font-bold text-primary">
                        ${vehiculo.precioBase.toLocaleString('en-US')}
                      </span>
                      {vehiculo.precioAdjudicado && (
                        <span className="text-sm text-muted-foreground">
                          (Adjudicado: ${vehiculo.precioAdjudicado.toLocaleString('en-US')})
                        </span>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleViewOfertas(vehiculo)}
                        className="gap-2"
                        data-testid="view-offers-card-button"
                      >
                        <TrendingUp className="h-4 w-4" />
                        Ofertas ({getOfertasCount(vehiculo.id)})
                      </Button>
                      <Button asChild variant="outline" size="sm" data-testid="view-detail-button">
                        <Link to={`/admin/publicaciones/${vehiculo.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          Ver Detalle
                        </Link>
                      </Button>
                      {vehiculo.estadoPublicacion === 'Borrador' && (
                        <Button asChild variant="outline" size="sm">
                          <Link to={`/admin/publicaciones/nueva?edit=${vehiculo.id}`}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </Link>
                        </Button>
                      )}
                      <Button variant="outline" size="sm">
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteId(vehiculo.id)}
                        data-testid="delete-button"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('vehiculo')}>
                      <div className="flex items-center gap-2">
                        Vehículo
                        {sortField === 'vehiculo' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('anio')}>
                      <div className="flex items-center gap-2">
                        Año
                        {sortField === 'anio' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('km')}>
                      <div className="flex items-center gap-2">
                        Km
                        {sortField === 'km' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('autor')}>
                      <div className="flex items-center gap-2">
                        Autor
                        {sortField === 'autor' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('precioBase')}>
                      <div className="flex items-center gap-2">
                        Precio
                        {sortField === 'precioBase' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium">Ofertas</th>
                    <th className="text-left p-4 font-medium">Estado</th>
                    <th className="text-left p-4 font-medium cursor-pointer hover:bg-accent/50" onClick={() => handleSort('createdAt')}>
                      <div className="flex items-center gap-2">
                        Fecha Pub.
                        {sortField === 'createdAt' ? (sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />) : <ArrowUpDown className="h-4 w-4 opacity-30" />}
                      </div>
                    </th>
                    <th className="text-right p-4 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehiculos.map((vehiculo) => (
                    <tr key={vehiculo.id} className="border-b last:border-0 hover:bg-accent/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                            {vehiculo.fotos && vehiculo.fotos[0] ? (
                              <img src={vehiculo.fotos[0]} alt={vehiculo.marca} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <FileText className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{vehiculo.marca} {vehiculo.modelo}</div>
                            <div className="text-sm text-muted-foreground">{vehiculo.version}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">{vehiculo.anio}</td>
                      <td className="p-4">{vehiculo.km.toLocaleString()}</td>
                      <td className="p-4 text-sm">{vehiculo.autor}</td>
                      <td className="p-4 font-medium">${vehiculo.precioBase.toLocaleString('en-US')}</td>
                      <td className="p-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOfertas(vehiculo)}
                          className="gap-2"
                          data-testid="view-offers-button"
                        >
                          <TrendingUp className="h-4 w-4" />
                          {getOfertasCount(vehiculo.id)}
                        </Button>
                      </td>
                      <td className="p-4">
                        <Badge variant={badgeVariants[vehiculo.estadoPublicacion]}>
                          {vehiculo.estadoPublicacion}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(vehiculo.createdAt).toLocaleDateString('es-UY', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button asChild variant="default" size="sm">
                            <Link to={`/admin/publicaciones/${vehiculo.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {vehiculo.estadoPublicacion === 'Borrador' && (
                            <Button asChild variant="outline" size="sm">
                              <Link to={`/admin/publicaciones/nueva?edit=${vehiculo.id}`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => setDeleteId(vehiculo.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Modal de Ofertas */}
      <Dialog open={!!selectedVehiculoOfertas} onOpenChange={() => setSelectedVehiculoOfertas(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Ofertas para {selectedVehiculoOfertas?.marca} {selectedVehiculoOfertas?.modelo}
            </DialogTitle>
            <DialogDescription>
              {selectedVehiculoOfertas?.anio} • {selectedVehiculoOfertas?.km?.toLocaleString()} km • 
              Precio Base: ${selectedVehiculoOfertas?.precioBase?.toLocaleString('en-US')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {selectedVehiculoOfertas && (
              <>
                {ofertas.filter(o => o.vehiculoId === selectedVehiculoOfertas.id).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No hay ofertas para este vehículo
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ofertas
                      .filter(o => o.vehiculoId === selectedVehiculoOfertas.id)
                      .map((oferta) => {
                        const revendedor = revendedores[oferta.revendedorId];
                        return (
                          <Card key={oferta.id} className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-medium text-lg">
                                  {revendedor?.nombre || 'Revendedor'}
                                </div>
                                {revendedor?.empresa && (
                                  <div className="text-sm text-muted-foreground">
                                    {revendedor.empresa}
                                  </div>
                                )}
                                {revendedor?.whatsapp && (
                                  <div className="text-sm text-muted-foreground">
                                    📱 {revendedor.whatsapp}
                                  </div>
                                )}
                              </div>
                              <Badge variant={badgeVariants[oferta.estado]}>
                                {oferta.estado}
                              </Badge>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-2xl font-bold text-primary">
                                  ${oferta.monto.toLocaleString('en-US')}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                  {new Date(oferta.hora).toLocaleString('es-UY', {
                                    dateStyle: 'short',
                                    timeStyle: 'short'
                                  })}
                                </div>
                              </div>

                              {oferta.estado === 'Pendiente' && selectedVehiculoOfertas.estadoPublicacion !== 'Adjudicada' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleAcceptOffer(oferta.id)}
                                    data-testid="modal-accept-offer-button"
                                  >
                                    <Check className="h-4 w-4 mr-1" />
                                    Aceptar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectOffer(oferta.id)}
                                    data-testid="modal-reject-offer-button"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Rechazar
                                  </Button>
                                </div>
                              )}
                            </div>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar publicación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La publicación y sus ofertas asociadas serán eliminadas permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteId)}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Publicaciones;